import React, { useState } from 'react';
import { Plus, Save, Calendar, MapPin, DollarSign, Users, FileText, Zap, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import { Label } from '../../ui/label';
import axios from 'axios';

const PostRequirement = () => {
  const [showPostOptions, setShowPostOptions] = useState(true);
  const [postType, setPostType] = useState(null); // 'detailed' or 'quick'
  const [showQuickPostForm, setShowQuickPostForm] = useState(false);
  
  const [quickFormData, setQuickFormData] = useState({
    programTitle: '',
    duration: '',
    startDate: '',
    endDate: '',
    location: '',
    budget: '',
    contactPerson: '',
    contactEmail: ''
  });
  
  const [formData, setFormData] = useState({
    // Program Details
    trainingArea: '',
    programTheme: '',
    programLevel: '',
    targetAudience: '',
    participantCount: '',
    programObjective: '',
    
    // Schedule Details
    scheduleType: 'tentative',
    month: '',
    year: new Date().getFullYear(),
    trainingMode: 'classroom',
    country: 'India',
    state: '',
    city: '',
    location: '',
    
    // Budget Details
    currency: 'USD',
    minAmount: '',
    maxAmount: '',
    budgetType: 'perProgram',
    
    // Organization Details
    organizationName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    aboutOrganization: ''
  });

  const trainingAreas = [
    'Functional', 'Leadership', 'Managerial & Behavioural', 'Technical'
  ];

  const programLevels = [
    'Basic', 'Intermediate', 'Advanced', 'All'
  ];

  const targetAudiences = [
    'WorkMen', 'Operators/Technician', 'Frontline Staff', 
    'Supervisors', 'Junior Management', 'Middle Management', 
    'Senior Management', 'Others'
  ];

  const participantCounts = [
    '1-10', '11-25', '26-50', '50 above'
  ];

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ];

  const years = Array.from({ length: 10 }, (_, i) => new Date().getFullYear() + i);

  const currencies = [
    'USD', 'EUR', 'GBP', 'INR', 'CAD', 'AUD'
  ];

  const indianStates = [
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
    'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
    'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan',
    'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh',
    'Uttarakhand', 'West Bengal'
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Posting requirement:', formData);
    alert('Training requirement posted successfully! You will receive proposals from trainers within 48 hours.');
    // Reset form or redirect
  };

  const handleQuickPostSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
      
      await axios.post(
        `${API_BASE_URL}/api/opportunities`,
        {
          program_title: quickFormData.programTitle,
          duration: quickFormData.duration,
          location: quickFormData.location,
          start_date: quickFormData.startDate || null,
          end_date: quickFormData.endDate || null,
          budget: quickFormData.budget,
          contact_person: quickFormData.contactPerson,
          contact_email: quickFormData.contactEmail
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      alert('✅ Training opportunity posted successfully! Trainers can now view and apply to your requirement.');
      setShowQuickPostForm(false);
      setShowPostOptions(true);
      // Reset form
      setQuickFormData({
        programTitle: '',
        duration: '',
        startDate: '',
        endDate: '',
        location: '',
        budget: '',
        contactPerson: '',
        contactEmail: ''
      });
    } catch (error) {
      console.error('Error posting opportunity:', error);
      alert('Failed to post opportunity: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handlePostTypeSelect = (type) => {
    setPostType(type);
    if (type === 'quick') {
      setShowQuickPostForm(true);
    } else {
      setShowPostOptions(false);
    }
  };

  // Show post type selection modal
  if (showPostOptions) {
    return (
      <>
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Plus className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Post Training Requirement</h1>
                <p className="text-gray-600">Choose how you'd like to post your requirement</p>
              </div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Quick Post Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-green-500"
              onClick={() => handlePostTypeSelect('quick')}
            >
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 bg-green-100 rounded-full">
                    <Zap className="w-8 h-8 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Quick Post</h3>
                    <p className="text-gray-600 mb-4">Fast and simple requirement posting with essential details</p>
                    <div className="text-sm text-left space-y-1 text-gray-600">
                      <p>✓ Program Title</p>
                      <p>✓ Duration & Dates</p>
                      <p>✓ Location</p>
                      <p>✓ Budget</p>
                      <p>✓ Contact Details</p>
                    </div>
                  </div>
                  <Button className="w-full bg-green-600 hover:bg-green-700">
                    <Zap className="w-4 h-4 mr-2" />
                    Quick Post (2 min)
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Detailed Post Option */}
            <Card 
              className="cursor-pointer hover:shadow-lg transition-all duration-200 border-2 hover:border-blue-500"
              onClick={() => handlePostTypeSelect('detailed')}
            >
              <CardContent className="p-6">
                <div className="flex flex-col items-center text-center space-y-4">
                  <div className="p-4 bg-blue-100 rounded-full">
                    <FileText className="w-8 h-8 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">Detailed Post</h3>
                    <p className="text-gray-600 mb-4">Comprehensive requirement with all specifications</p>
                    <div className="text-sm text-left space-y-1 text-gray-600">
                      <p>✓ All Quick Post fields</p>
                      <p>✓ Training Area & Theme</p>
                      <p>✓ Target Audience</p>
                      <p>✓ Program Objectives</p>
                      <p>✓ Organization Details</p>
                    </div>
                  </div>
                  <Button className="w-full bg-blue-600 hover:bg-blue-700">
                    <FileText className="w-4 h-4 mr-2" />
                    Detailed Post (5 min)
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Quick Post Form Modal */}
        <Dialog open={showQuickPostForm} onOpenChange={(open) => {
          setShowQuickPostForm(open);
          if (!open) {
            setPostType(null);
          }
        }}>
          <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <DialogHeader>
              <DialogTitle className="flex items-center">
                <Zap className="w-5 h-5 mr-2 text-green-600" />
                Quick Post Training Requirement
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleQuickPostSubmit} className="space-y-4 py-4">
              <div>
                <Label htmlFor="programTitle">Tentative Program Title *</Label>
                <Input
                  id="programTitle"
                  placeholder="e.g., Leadership Development Workshop"
                  value={quickFormData.programTitle}
                  onChange={(e) => setQuickFormData({...quickFormData, programTitle: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="duration">Duration *</Label>
                  <Input
                    id="duration"
                    placeholder="e.g., 2 days"
                    value={quickFormData.duration}
                    onChange={(e) => setQuickFormData({...quickFormData, duration: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location *</Label>
                  <Input
                    id="location"
                    placeholder="e.g., Mumbai, India"
                    value={quickFormData.location}
                    onChange={(e) => setQuickFormData({...quickFormData, location: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="startDate">Probable Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={quickFormData.startDate}
                    onChange={(e) => setQuickFormData({...quickFormData, startDate: e.target.value})}
                  />
                </div>
                <div>
                  <Label htmlFor="endDate">Probable End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={quickFormData.endDate}
                    onChange={(e) => setQuickFormData({...quickFormData, endDate: e.target.value})}
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="budget">Approximate Budget *</Label>
                <Input
                  id="budget"
                  placeholder="e.g., $5,000 - $7,000"
                  value={quickFormData.budget}
                  onChange={(e) => setQuickFormData({...quickFormData, budget: e.target.value})}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="contactPerson">Contact Person *</Label>
                  <Input
                    id="contactPerson"
                    placeholder="Full Name"
                    value={quickFormData.contactPerson}
                    onChange={(e) => setQuickFormData({...quickFormData, contactPerson: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="contactEmail">Contact Email *</Label>
                  <Input
                    id="contactEmail"
                    type="email"
                    placeholder="email@company.com"
                    value={quickFormData.contactEmail}
                    onChange={(e) => setQuickFormData({...quickFormData, contactEmail: e.target.value})}
                    required
                  />
                </div>
              </div>

              <div className="flex justify-end space-x-2 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowQuickPostForm(false);
                    setPostType(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="bg-green-600 hover:bg-green-700">
                  <Save className="w-4 h-4 mr-2" />
                  Post Requirement
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Show detailed form if detailed post type selected
  if (postType === 'detailed') {
    return (
      <div className="space-y-6">
        {/* Header with Back Button */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Button 
              variant="ghost" 
              onClick={() => {
                setPostType(null);
                setShowPostOptions(true);
              }}
            >
              ← Back
            </Button>
            <div className="p-2 bg-blue-100 rounded-lg">
              <Plus className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Post Training Requirement (Detailed)</h1>
              <p className="text-gray-600">Provide comprehensive details for better trainer proposals</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
        {/* Program Details Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Users className="w-5 h-5 mr-2 text-blue-600" />
              Program Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Area of Training *
                </label>
                <select
                  value={formData.trainingArea}
                  onChange={(e) => handleInputChange('trainingArea', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Area</option>
                  {trainingAreas.map(area => (
                    <option key={area} value={area}>{area}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Program Level *
                </label>
                <select
                  value={formData.programLevel}
                  onChange={(e) => handleInputChange('programLevel', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Level</option>
                  {programLevels.map(level => (
                    <option key={level} value={level}>{level}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Theme *
              </label>
              <input
                type="text"
                value={formData.programTheme}
                onChange={(e) => handleInputChange('programTheme', e.target.value)}
                placeholder="e.g., Leadership Development, Team Building, Technical Skills"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Target Audience *
                </label>
                <select
                  value={formData.targetAudience}
                  onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Audience</option>
                  {targetAudiences.map(audience => (
                    <option key={audience} value={audience}>{audience}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of Participants *
                </label>
                <select
                  value={formData.participantCount}
                  onChange={(e) => handleInputChange('participantCount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Count</option>
                  {participantCounts.map(count => (
                    <option key={count} value={count}>{count}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Program Objective *
              </label>
              <textarea
                value={formData.programObjective}
                onChange={(e) => handleInputChange('programObjective', e.target.value)}
                placeholder="Describe the learning objectives and expected outcomes..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                required
              />
            </div>
          </CardContent>
        </Card>

        {/* Schedule & Location Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-green-600" />
              Schedule & Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Schedule Status *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="tentative"
                    checked={formData.scheduleType === 'tentative'}
                    onChange={(e) => handleInputChange('scheduleType', e.target.value)}
                    className="mr-2"
                  />
                  Tentative
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="confirmed"
                    checked={formData.scheduleType === 'confirmed'}
                    onChange={(e) => handleInputChange('scheduleType', e.target.value)}
                    className="mr-2"
                  />
                  Confirmed
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Month *
                </label>
                <select
                  value={formData.month}
                  onChange={(e) => handleInputChange('month', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select Month</option>
                  {months.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year *
                </label>
                <select
                  value={formData.year}
                  onChange={(e) => handleInputChange('year', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {years.map(year => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Training Mode *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="classroom"
                    checked={formData.trainingMode === 'classroom'}
                    onChange={(e) => handleInputChange('trainingMode', e.target.value)}
                    className="mr-2"
                  />
                  Classroom
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="virtual"
                    checked={formData.trainingMode === 'virtual'}
                    onChange={(e) => handleInputChange('trainingMode', e.target.value)}
                    className="mr-2"
                  />
                  Virtual
                </label>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  State *
                </label>
                <select
                  value={formData.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select State</option>
                  {indianStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  City *
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                  placeholder="Enter city name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Specific Location
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => handleInputChange('location', e.target.value)}
                placeholder="Venue address or specific location details"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Budget Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <DollarSign className="w-5 h-5 mr-2 text-green-600" />
              Budget Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Currency *
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => handleInputChange('currency', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  {currencies.map(currency => (
                    <option key={currency} value={currency}>{currency}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Min Amount *
                </label>
                <input
                  type="number"
                  value={formData.minAmount}
                  onChange={(e) => handleInputChange('minAmount', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Max Amount *
                </label>
                <input
                  type="number"
                  value={formData.maxAmount}
                  onChange={(e) => handleInputChange('maxAmount', e.target.value)}
                  placeholder="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Budget Type *
              </label>
              <div className="flex space-x-4">
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="perDay"
                    checked={formData.budgetType === 'perDay'}
                    onChange={(e) => handleInputChange('budgetType', e.target.value)}
                    className="mr-2"
                  />
                  Per Day
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="perPerson"
                    checked={formData.budgetType === 'perPerson'}
                    onChange={(e) => handleInputChange('budgetType', e.target.value)}
                    className="mr-2"
                  />
                  Per Person
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    value="perProgram"
                    checked={formData.budgetType === 'perProgram'}
                    onChange={(e) => handleInputChange('budgetType', e.target.value)}
                    className="mr-2"
                  />
                  Per Program
                </label>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Organization Details Section */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <MapPin className="w-5 h-5 mr-2 text-purple-600" />
              Organization Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Organization Name *
                </label>
                <input
                  type="text"
                  value={formData.organizationName}
                  onChange={(e) => handleInputChange('organizationName', e.target.value)}
                  placeholder="Your organization name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Person Name *
                </label>
                <input
                  type="text"
                  value={formData.contactPerson}
                  onChange={(e) => handleInputChange('contactPerson', e.target.value)}
                  placeholder="Full name of contact person"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address *
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="contact@organization.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mobile Number *
                </label>
                <input
                  type="tel"
                  value={formData.mobile}
                  onChange={(e) => handleInputChange('mobile', e.target.value)}
                  placeholder="+91 9876543210"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                About Organization
              </label>
              <textarea
                value={formData.aboutOrganization}
                onChange={(e) => handleInputChange('aboutOrganization', e.target.value)}
                placeholder="Brief description of your organization and training context..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </CardContent>
        </Card>

        {/* Submit Section */}
        <div className="flex justify-end space-x-4">
          <Button type="button" variant="outline" size="lg">
            Save as Draft
          </Button>
          <Button type="submit" size="lg" className="bg-blue-600 hover:bg-blue-700">
            <Save className="w-4 h-4 mr-2" />
            Post Requirement
          </Button>
        </div>
      </form>
    </div>
    );
  }

  return null;
};

export default PostRequirement;